import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, message, Modal, Popconfirm, Row, Space, Table, Typography, Grid, Upload, Tooltip, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CloudOutlined, BookOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';
import {
   getCourses,
   createCourse,
   updateCourse,
   deleteCourse,
   getTopicsByCourse,
   createTopic,
   updateTopic,
   deleteTopic,
} from '../../services/MicrocredentialsCourseTopicService';
import ResponsiveBreadcrumb from '../../components/breadcrumb/ResponsiveBreadcrumb';
import ResponsivePdfViewer from '../../components/document-viewer/ResponsivePdfViewer';
import '../../pages/common/BraineeModule.css';

const { Title, Text } = Typography;

const emptyCourseForm = { name: '', code: '', description: '' };
const emptyTopicForm = { title: '', description: '' };

export default function MicrocredentialsCourseTopicManager() {
   const userRole = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const isSuperAdmin = userRole === 'Super Admin';

   const [loadingCourses, setLoadingCourses] = useState(false);
   const [courses, setCourses] = useState([]);
   const [selectedCourse, setSelectedCourse] = useState(null);

   const [loadingTopics, setLoadingTopics] = useState(false);
   const [topics, setTopics] = useState([]);

   const [courseModalOpen, setCourseModalOpen] = useState(false);
   const [topicModalOpen, setTopicModalOpen] = useState(false);

   const [editingCourse, setEditingCourse] = useState(null);
   const [editingTopic, setEditingTopic] = useState(null);
   const [teacherGuideFile, setTeacherGuideFile] = useState(null);
   const [teacherGuidePreviewOpen, setTeacherGuidePreviewOpen] = useState(false);
   const [teacherGuidePreviewUrl, setTeacherGuidePreviewUrl] = useState('');

   const [processing, setProcessing] = useState(false);

   const [courseForm] = Form.useForm();
   const [topicForm] = Form.useForm();

   const screens = Grid.useBreakpoint();
   const getSelectedCourseId = () => selectedCourse?.course_id ?? selectedCourse?.id;

   useEffect(() => {
      if (!isSuperAdmin) return;
      fetchCourses();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isSuperAdmin]);

   useEffect(() => {
      const courseId = getSelectedCourseId();
      if (!courseId) {
         setTopics([]);
         return;
      }
      fetchTopics(courseId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedCourse?.id, selectedCourse?.course_id]);

   const fetchCourses = async () => {
      try {
         setLoadingCourses(true);
         const data = await getCourses();
         const list = Array.isArray(data) ? data : [];
         setCourses(list);
         if (list.length > 0 && !selectedCourse) setSelectedCourse(list[0]);
      } catch (error) {
         message.error('Failed to load courses');
      } finally {
         setLoadingCourses(false);
      }
   };

   const fetchTopics = async (courseId) => {
      try {
         setLoadingTopics(true);
         const data = await getTopicsByCourse(courseId);
         setTopics(Array.isArray(data) ? data : []);
      } catch (error) {
         message.error('Failed to load topics');
      } finally {
         setLoadingTopics(false);
      }
   };

   const openAddCourse = () => {
      setEditingCourse(null);
      courseForm.setFieldsValue(emptyCourseForm);
      setCourseModalOpen(true);
   };

   const openEditCourse = (record) => {
      setEditingCourse(record);
      courseForm.setFieldsValue({
         name: record.name || '',
         code: record.code || '',
         description: record.description || '',
      });
      setCourseModalOpen(true);
   };

   const onTeacherGuideFileChange = (file) => {
      const isPdf = file?.type === 'application/pdf' || (file?.name || '').toLowerCase().endsWith('.pdf');
      if (!isPdf) {
         message.error('Please select a PDF file for the teacher guide.');
         return false;
      }

      setTeacherGuideFile(file);
      return false;
   };

   const onSubmitCourse = async () => {
      try {
         setProcessing(true);
         const values = await courseForm.validateFields();
         const formData = new FormData();

         Object.entries(values).forEach(([key, value]) => {
            formData.append(key, value ?? '');
         });

         const courseId = editingCourse?.course_id ?? editingCourse?.id;
         if (courseId) {
            await updateCourse(courseId, formData);
            message.success('Course updated');
         } else {
            await createCourse(formData);
            message.success('Course created');
         }
         
         setCourseModalOpen(false);
         setProcessing(false);
         await fetchCourses();
      } catch (error) {
         if (error?.errorFields) return;
         message.error('Unable to save course');
         setProcessing(false);
      }
   };

   const onDeleteCourse = async (courseId) => {
      try {
         await deleteCourse(courseId);
         message.success('Course deleted');
         if (selectedCourse?.course_id === courseId) setSelectedCourse(null);
         await fetchCourses();
      } catch (error) {
         message.error('Unable to delete course');
      }
   };
   const getTeacherGuidePdfUrl = (record) => {
      const guideValue = record?.teacher_guide ?? record?.Teacher_guide ?? record?.teacherGuide ?? record?.TeacherGuide;
      if (!guideValue) return '';

      const value = String(guideValue).trim();
      if (!value) return '';
      if (/^https?:\/\//i.test(value)) return value;

      // const subdomain = localStorage.getItem('lms-subdomain') || '';
      // return `${process.env.REACT_APP_API_S3_URL}/${subdomain}/${value}`;
      return `${process.env.REACT_APP_API_S3_URL}/${value}`;
   };

   const openTeacherGuidePreview = (record) => {
      const pdfUrl = getTeacherGuidePdfUrl(record);
      if (!pdfUrl) {
         message.warning('No teacher guide PDF is available for this topic.');
         return;
      }

      setTeacherGuidePreviewUrl(pdfUrl);
      setTeacherGuidePreviewOpen(true);
   };
   const openAddTopic = () => {
      if (!getSelectedCourseId()) return;
      setEditingTopic(null);
      setTeacherGuideFile(null);
      topicForm.setFieldsValue(emptyTopicForm);
      setTopicModalOpen(true);
   };

   const openEditTopic = (record) => {
      setEditingTopic(record);
      setTeacherGuideFile(null);
      topicForm.setFieldsValue({
         topic_id: record.topic_id || record.id || '',
         title: record.title || '',
         description: record.description || '',
      });
      setTopicModalOpen(true);
   };

   const onSubmitTopic = async () => {
      setProcessing(true);
      const course_id = getSelectedCourseId();

      if (!course_id) {
         setProcessing(false);
         return;
      }
      
      try {
         const values = await topicForm.validateFields();
         const payload = new FormData();
         payload.append('course_id', course_id);
         payload.append('title', values.title);
         payload.append('description', values.description ?? '');
         if (teacherGuideFile) {
            payload.append('teacher_guide', teacherGuideFile, teacherGuideFile.name);
         }

         if (editingTopic) {
            await updateTopic(editingTopic.id ?? editingTopic.topic_id, payload);
            message.success('Topic updated');
         } else {
            await createTopic(payload);
            message.success('Topic created');
         }

         setTopicModalOpen(false);
         setTeacherGuideFile(null);
         setProcessing(false);
         await fetchTopics(course_id);
      } catch (error) {
         setProcessing(false);
         if (error?.errorFields) return;
         message.error('Unable to save topic');
      }
   };

   const onDeleteTopic = async (topicId) => {
      const course_id = getSelectedCourseId();
      if (!course_id) return;
      try {
         await deleteTopic(topicId);
         message.success('Topic deleted');
         await fetchTopics(course_id);
      } catch (error) {
         message.error('Unable to delete topic');
      }
   };

   const courseColumns = [
         // { title: 'Code', dataIndex: 'code', key: 'code', width: 80 },
         { title: 'Name', dataIndex: 'name', key: 'name' },
         {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
               <Space>                  
                  <EditOutlined onClick={() => openEditCourse(record)} />
                  <Popconfirm title="Delete this course?" onConfirm={() => onDeleteCourse(record.course_id ?? record.id)}>
                     <DeleteOutlined />
                  </Popconfirm>
               </Space>
            ),
         },
      ];

   const topicColumns = [
         { title: 'Title', dataIndex: 'title', key: 'title' },
         { title: 'Description', dataIndex: 'description', key: 'description', width: 280 },
         {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
               <Space>
                  <EditOutlined onClick={() => openEditTopic(record)} />
                  {getTeacherGuidePdfUrl(record) && (
                     <Tooltip title="Preview Teacher Guide">
                        <EyeOutlined onClick={() => openTeacherGuidePreview(record)} />
                     </Tooltip>
                  )}
                  <Popconfirm title="Delete this topic?" onConfirm={() => onDeleteTopic(record.topic_id)}>
                     <DeleteOutlined />
                  </Popconfirm>
               </Space>
            ),
         },
      ];

   if (!isSuperAdmin) {
      return (
         <Card className="brainee-module-panel">
            <Title level={4}>Microcredentials Course & Topic Management</Title>
            <Text type="danger">You are not authorized to access this page.</Text>
         </Card>
      );
   }

   return (
      <div className="dashboard-area-all management-page">
         <div className="dashboard-item management-shell">
            <div className="management-breadcrumb">
               <ResponsiveBreadcrumb
                  items={[
                     { key: 'braineemicrocreds', node: 'Brainee Microcredentials' },
                     { key: 'microcredentials-course-topics', node: 'Course & Topic Management' },
                  ]}
               />
            </div>
            <section className="brainee-module-hero">
               <div>
                  <span className="brainee-module-kicker">Brainee Microcredentials</span>
                  <h1>Course &amp; Topic Management</h1>
                  <p>Manage microcredential courses and their topics, organize learning paths, and maintain the curriculum.</p>
                  <div className="brainee-module-meta">
                     <span><CloudOutlined /> {courses.length} courses</span>
                     <span><BookOutlined /> {topics.length} topics</span>
                  </div>
               </div>
            </section>
            <Row gutter={16} className={screens.xs ? '' : 'page-padding'}>
               <Col xs={24} lg={10}>
                  <Card
                     style={{ height: 'calc(100vh - 300px)' }}
                     title={<span><CloudOutlined /> Courses</span>}
                     extra={
                        <div className="management-action-row">
                           <div className="management-action-group">
                              <Button className='button-shadow' type='primary' shape='round' icon={<PlusOutlined />} onClick={openAddCourse}>
                                 Add Course
                              </Button>
                           </div>
                        </div>
                     }
                     className="brainee-module-panel"
                  >
                     <div className="brainee-module-table-shell">
                        <Table
                           rowKey="id"
                           loading={loadingCourses}
                           dataSource={courses}
                           columns={courseColumns}
                           size="small"
                           pagination={{ pageSize: 8 }}
                           onRow={(record) => ({
                              onClick: () => setSelectedCourse(record),
                              style: {
                                 cursor: 'pointer',
                                 backgroundColor: selectedCourse?.id === record.id ? '#f0f7ff' : undefined,
                              },
                           })}
                        />
                     </div>
                  </Card>
               </Col>
               <Col xs={24} lg={14}>
                  <Card
                     style={{ height: 'calc(100vh - 300px)' }}
                     title={selectedCourse ? <span><BookOutlined /> Topics - {selectedCourse.name}</span> : <span><BookOutlined /> Topics</span>}
                     extra={
                        <div className="management-action-row">
                           <div className="management-action-group">
                              <Button className='button-shadow' type='primary' shape='round' icon={<PlusOutlined />} onClick={openAddTopic} disabled={!selectedCourse}>
                                 Add Topic
                              </Button>
                           </div>
                        </div>
                     }
                     className="brainee-module-panel"
                  >
                     <div className="brainee-module-table-shell">
                        <Table
                           rowKey="id"
                           loading={loadingTopics}
                           dataSource={topics}
                           columns={topicColumns}
                           size="small"
                           pagination={{ pageSize: 10 }}
                        />
                     </div>
                  </Card>
               </Col>
            </Row>
            {/* Course Modal */}
            <Modal
               open={teacherGuidePreviewOpen}
               title="Teacher Guide Preview"
               onCancel={() => setTeacherGuidePreviewOpen(false)}
               okButtonProps={{ style: { display: 'none' } }}
               cancelText="Close"
               width={900}
               destroyOnClose
            >
               {teacherGuidePreviewUrl ? (
                  <div style={{ height: '60vh' }}>
                     <ResponsivePdfViewer fileUrl={teacherGuidePreviewUrl} height="100%" />
                  </div>
               ) : null}
            </Modal>

            <Modal
               open={courseModalOpen}
               title={editingCourse ? 'Edit Course' : 'Add Course'}
               onOk={onSubmitCourse}
               onCancel={() => setCourseModalOpen(false)}
               destroyOnClose
            >
               <Spin spinning={processing} tip="Processing request..." size="medium">
                  <Form form={courseForm} layout="vertical">
                     <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Code is required' }]}>
                        <Input />
                     </Form.Item>
                     <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                        <Input />
                     </Form.Item>
                     <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} />
                     </Form.Item>
                  </Form>
               </Spin>
            </Modal>

            {/* Topic Modal */}
            <Modal
               open={topicModalOpen}
               title={editingTopic ? 'Edit Topic' : 'Add Topic'}
               onOk={onSubmitTopic}
               onCancel={() => setTopicModalOpen(false)}
               destroyOnClose
            >
               <Spin spinning={processing} tip="Processing request..." size="medium">
                  <Form form={topicForm} layout="vertical" className="brainee-module-panel">
                     <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
                        <Input />
                     </Form.Item>
                     <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} />
                     </Form.Item>
                     <Form.Item label="Teacher Guide PDF">
                        <Upload
                           accept=".pdf,application/pdf"
                           showUploadList={false}
                           beforeUpload={onTeacherGuideFileChange}
                        >
                           <Button icon={<UploadOutlined />} shape="round" size="small">
                              Select PDF
                           </Button>
                        </Upload>
                        <div style={{ marginTop: 8 }}>
                           {teacherGuideFile ? (
                              <Text type="success">Selected: {teacherGuideFile.name}</Text>
                           ) : editingTopic && getTeacherGuidePdfUrl(editingTopic) ? (
                              <Text type="secondary">A guide is already uploaded. Select a PDF to replace it.</Text>
                           ) : (
                              <Text type="secondary">No PDF selected</Text>
                           )}
                        </div>
                     </Form.Item>
                  </Form>
               </Spin>
            </Modal>
         </div>
      </div>
   );
}
